import { Notification } from '@thanx/uikit/notification'
import { Text } from '@thanx/uikit/text'
import { useGetRecoveryConfigQuery } from 'api/recoveryAi'
import ConfirmModal from 'components/ConfirmModal'
import { buildTranslate } from 'locales'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import Helmet from 'react-helmet'
import { useDispatch } from 'react-redux'
import { Redirect, RouteComponentProps } from 'react-router-dom'
import { push } from 'react-router-redux'
import { useStyletron } from 'styletron-react'
import { merchantTheme } from 'theme'
import { isRecoveryActive } from '../recoveryStatus'
import {
  ACTIVATION_PATH,
  RECOVERY_AI_ROOT,
  SETTINGS_PATH,
  WIZARD_RETURN_TO_ACTIVATION,
  WIZARD_RETURN_TO_PARAM,
} from '../routes'
import {
  APP_NAVBAR_HEIGHT,
  WIZARD_CONTENT_MAX_WIDTH,
  WIZARD_STICKY_BAR_Z_INDEX,
} from './constants'
import QuickStart from './QuickStart'
import EscalationRouting from './steps/EscalationRouting'
import LocationsStep from './steps/LocationsStep'
import PhilosophyAssessment, {
  isAssessmentComplete,
} from './steps/PhilosophyAssessment'
import Processing from './steps/Processing'
import Testing from './steps/Testing'
import { useActivationCompleteness } from './useActivationCompleteness'
import { useWizardSubmit } from './useWizardSubmit'
import { useWizardState } from './WizardContext'
import WizardNavButtons from './WizardNavButtons'
import WizardStepper, { buildConfigurePath, STEPS } from './WizardStepper'

const t = buildTranslate('recovery_ai')
const tWizard = buildTranslate('recovery_ai.wizard')

const VALID_SLUGS: string[] = STEPS.map(s => s.slug)

// The "Configuring your RecoveryAI" calibration interstitial is a non-STEPS
// route reached after Train and before Testing (AI-242 / FR-14). One literal,
// shared by Train's Continue hop below and matched by the route handler above.
const PROCESSING_PATH = '/recovery_ai/configure/processing'

// id of the config-load-error cue (AI-580), wired to the disabled Finalize the
// same way when a re-entry's config GET failed.
const CONFIG_LOAD_ERROR_CUE_ID = 'rai-config-load-error'

// ENG-984 (WCAG 4.1.3): id of the sr-only note explaining why Finalize is
// disabled while activation is incomplete — under D2, no reachable human on
// escalate yet, or the gate's sources are still unsettled. The VISIBLE "finish
// these steps" checklist banner was removed on this step (product ask); this
// restores a non-visual programmatic reason so the disabled state isn't silent
// to assistive tech. Mirrors the mechanism used by Processing's
// CONTINUE_CUE_ID (id'd sr-only span ← aria-describedby).
const ACTIVATION_INCOMPLETE_CUE_ID = 'rai-activation-incomplete'

// id of the sr-only note explaining why Continue is disabled on the Locations
// step when the merchant picked "a subset of locations" but selected none. Same
// mechanism as the Finalize cues (id'd sr-only span ← aria-describedby).
const LOCATIONS_EMPTY_SUBSET_CUE_ID = 'rai-locations-empty-subset'

type Props = RouteComponentProps<{ step: string }>

const Configure = ({ match, location }: Props) => {
  const [css] = useStyletron()
  const dispatch = useDispatch()
  // AI-892: ConfiguredSummary sends an editor here with ?returnTo=activation to
  // preview the sandbox. When set, Cancel returns to the activation summary
  // instead of push('/recovery_ai') — which for a configured-but-off merchant
  // lands on the promo page (Landing's activation redirect sits behind the promo
  // gate), the dead end the ticket removes. Scoped to Cancel on the entered step;
  // Continue's Finalize already routes to ACTIVATION_PATH.
  const returnsToActivation =
    new URLSearchParams(location.search).get(WIZARD_RETURN_TO_PARAM) ===
    WIZARD_RETURN_TO_ACTIVATION
  const { step } = match.params
  const { state: wizardState, update, continueActionRef } = useWizardState()
  const { submitStep, isSubmitting } = useWizardSubmit()
  const [submitError, setSubmitError] = useState<string | null>(null)
  // AI-1130: true while persistStep is running — folded into the Continue
  // button's isSubmitting prop below so a merchant can't double-click it while
  // a registered step action (e.g. LocationsStep's location_configs PUT) is
  // still in flight. useWizardSubmit's own isSubmitting only reflects the
  // generic config-PUT mutation, which the Locations step's writes don't use.
  const [isPersistingStep, setIsPersistingStep] = useState(false)
  // AI-996: which pending leave-the-step action is awaiting confirmation, or null
  // when no confirm dialog is open. Back and Cancel re-derive the Locations stage
  // from saved truth on re-entry, so leaving with an unsaved per-location edit
  // silently discards it — the same failure the Continue gate closes. Guard both
  // behind a confirm dialog (see handleBack/handleCancel below).
  const [pendingLeave, setPendingLeave] = useState<'back' | 'cancel' | null>(
    null
  )
  // AI-580: the config GET drives wizard hydration (in WizardProvider). If it
  // NEVER loaded, hydration never ran, so wizard state holds DEFAULTS — a
  // returning merchant's Finalize would re-PUT those over live config. Read the
  // shared query (RTK dedupes the request) to gate Finalize below. Gate on
  // never-loaded (error AND no data), NOT isError alone: RTK RETAINS the last-good
  // `data` across a failed REFETCH (e.g. the config invalidation the Locations
  // Save fires), so `isError` can be true on a wizard that already hydrated fine —
  // blocking then would be a false positive. A first-time merchant is unaffected:
  // the GET find_or_initializes a 200 default config, so `!config` only holds on a
  // genuine initial-load failure.
  const { data: config, isError: isConfigLoadError } =
    useGetRecoveryConfigQuery()
  const configNeverLoaded = !!isConfigLoadError && !config

  // Is the merchant ALREADY active (onboarded + a running mode)? Shared
  // isRecoveryActive (single source with Landing — the guard breaks if the two
  // derivations drift). Finalize omits operating_mode, so an already-active
  // re-entry stays active — it must route back to the console, NOT the activate
  // CTA, which would re-fire the audited PUT on an already-running agent
  // (true→true is not an enable transition) while showing a false "activated"
  // success with no provisioning to show for it.
  const isAlreadyActive = isRecoveryActive(config)

  const currentIndex = STEPS.findIndex(s => s.slug === step)
  const isLastStep = currentIndex === STEPS.length - 1
  // AI-173 / D2: gate Finalize on activation completeness so onboarding can't
  // complete with no way to reach a human — a PRIMARY escalation recipient, or
  // notify-location-managers on. A first-time merchant may activate WITHOUT
  // approved rewards (picked later in Settings → Approved Rewards), so the
  // reward catalog never gates Finalize. Skip the gate's fetches until the last
  // step, where it is actually evaluated.
  const { isComplete: isActivationComplete, blockingReason } =
    useActivationCompleteness(
      { selectedRewardIds: wizardState.selectedRewardIds },
      { skip: !isLastStep }
    )

  // AI-160: mark the Testing stage complete when the merchant finishes the
  // 3-persona guided playback (the simulator's Complete CTA). useCallback-stable
  // so the reference never churns WizardSimulator/PlaybackNav across renders
  // (FE-1 D6 referential-stability contract). testingComplete is session-only —
  // useWizardSubmit maps 'testing' to {}, so nothing is persisted.
  const handleTestingComplete = useCallback(
    () => update({ testingComplete: true }),
    [update]
  )

  // AI-117: move keyboard focus to the step content on every step change so nav
  // (Back/Continue/Skip) doesn't leave focus stranded on the clicked button —
  // screen readers announce the new step, keyboard users get a focus anchor
  // (WCAG 2.4.3 / 3.2.1). Configure re-renders (not remounts) per :step, so one
  // shared effect keyed on the slug fires on each navigation; on non-STEPS
  // early-return routes the wrapper is absent, so the ref is null and this no-ops.
  const stepContentRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    stepContentRef.current?.focus()
  }, [step])

  // Quick-Start is the R12 onboarding intro (stage 0): a non-STEPS,
  // non-persisting route rendered standalone (no stepper chrome), so it is
  // handled before the STEPS-slug validation below.
  if (step === 'quick-start') {
    return <QuickStart />
  }

  // Processing is the "Configuring your RecoveryAI" calibration interstitial: a
  // non-STEPS, non-persisting route shown after Train and before Testing (AI-242
  // / FR-14). It renders its own teal chrome and advances to the Testing step
  // after a fixed delay. Handled before the STEPS-slug validation. Reachable only
  // within the session that just finished Train: the gate keys on the session-only
  // assessmentAnswers, so a direct URL hit — or a page reload, which resets that
  // session state — before Train is complete redirects out to the wizard landing
  // instead of showing calibration. (Surviving a mid-flow reload would require
  // persisting Train completion server-side; Train's Continue persists nothing
  // today, so that is deferred to a follow-up — see the redirect below.)
  //
  // ENG-964 (supersedes AI-322, LOCKED 2026-07-08): Train is no longer
  // skippable — the quiz is mandatory, so every merchant reaching /processing
  // via Continue has a complete assessment. This redirect guard stays as
  // defense-in-depth for a direct URL hit or a mid-flow page reload (which
  // resets the session-only assessmentAnswers) before Train is complete.
  if (step === 'processing') {
    if (isAssessmentComplete(wizardState.assessmentAnswers)) {
      return <Processing />
    }
    return <Redirect to="/recovery_ai" />
  }

  if (!VALID_SLUGS.includes(step)) {
    return <Redirect to={buildConfigurePath('train')} />
  }

  // ENG-964: Train (the recovery-philosophy quiz) is mandatory — no Skip
  // affordance. Continue is gated until all three questions are answered
  // (partial answers would persist incomplete scoring intent).
  const blockTrainContinue =
    step === 'train' && !isAssessmentComplete(wizardState.assessmentAnswers)

  // AI-160: gate the Testing stage's Continue until the merchant completes the
  // guided playback. Kept independent of blockTrainContinue and blockFinalize
  // (different stages, different flags) — they are OR-combined below, never
  // merged into one condition.
  const blockTestingContinue =
    step === 'testing' && !wizardState.testingComplete

  // Locations: block Continue when the merchant chose "a subset of locations"
  // but deselected every one — advancing would leave RecoveryAI configured to
  // run at no location at all. "All locations" mode is never blocked — an empty
  // selection list there still means everywhere.
  const blockLocationsEmptySubset =
    step === 'locations' &&
    wizardState.locationMode === 'subset' &&
    wizardState.selectedLocationIds.length === 0

  // AI-1130: Continue no longer blocks on an unsaved Locations edit — it now
  // PERSISTS the edit itself via continueActionRef before advancing (see
  // persistStep below), so there is nothing left to silently discard. This flag
  // (renamed off "block*" since it no longer blocks Continue) still drives the
  // Back/Cancel "discard changes?" confirm dialog further down
  // (leaveWouldDiscardEdit) — leaving via Back/Cancel deliberately does NOT
  // persist, so that guard is unchanged.
  const locationsHasUnsavedEdit =
    step === 'locations' && wizardState.locationsUnsaved
  const blockLocationsContinue = blockLocationsEmptySubset

  // Finalize is blocked on the last stage until activation is complete — a
  // reachable human on escalate (D2). useActivationCompleteness owns that
  // verdict as a single source, and also names WHY, for the cue below.
  const blockFinalize = isLastStep && !isActivationComplete
  // AI-580: also block Finalize when the config never loaded — the wizard never
  // hydrated the merchant's saved settings, so re-PUTing now would clobber live
  // config with defaults. Independent of the activation gate (which may be
  // satisfied anyway); OR-combined into continueDisabled below.
  const blockFinalizeUnhydrated = isLastStep && configNeverLoaded

  // ENG-984: the "finish these steps to activate your agent" checklist banner
  // was removed from this step (product ask), so the disabled state needs a
  // non-visual programmatic reason (WCAG 4.1.3): ACTIVATION_INCOMPLETE_CUE_ID,
  // an sr-only span rendered below (mirrors Processing's CONTINUE_CUE_ID).
  // Programmatic reason for the disabled Continue/Finalize, wired via
  // aria-describedby. The blocks are mutually exclusive by step (locations vs
  // last-step finalize), so order only reads as precedence — except the two
  // last-step ones: the config-load-error cue wins, because a re-entry whose
  // config never hydrated can't safely Finalize whatever the recipient gate
  // says, and "refresh and retry" is the more actionable message.
  const getContinueCueId = (): string | undefined => {
    if (blockLocationsEmptySubset) return LOCATIONS_EMPTY_SUBSET_CUE_ID
    if (blockFinalizeUnhydrated) return CONFIG_LOAD_ERROR_CUE_ID
    if (blockFinalize) return ACTIVATION_INCOMPLETE_CUE_ID
    return undefined
  }
  const continueCueId = getContinueCueId()

  // Visible + keyboard-accessible hover tooltip (TooltipWhenDisabled) naming
  // the Locations-stage empty-subset blocking reason at a glance. Scoped to
  // Locations only — Train/Testing/Finalize keep their existing sr-only-only
  // cues, unrequested here.
  const getContinueTooltip = (): string | undefined => {
    if (blockLocationsEmptySubset)
      return tWizard('locations.continue_tooltip_empty_subset')
    return undefined
  }
  const continueTooltip = getContinueTooltip()

  // The activation cue's TEXT, one key per blocking reason (D2). Distinct
  // messages, not variations: a merchant mid-save or whose recipients failed to
  // load has nothing to fix in the form, so "pick a recipient" would misdirect
  // them. Only ever read when blockFinalize is the active cue, so the null case
  // is unreachable in render — it returns the recipient copy rather than an
  // empty string so a future caller can't render a nameless cue.
  const resolveActivationCueText = (): string => {
    switch (blockingReason) {
      case 'loading':
        return tWizard('escalations.finalize_checking_recipients')
      case 'saving':
        return tWizard('escalations.finalize_saving_recipients')
      case 'save_failed':
        return tWizard('escalations.finalize_save_failed')
      case 'unavailable':
        return tWizard('escalations.finalize_recipients_unavailable')
      default:
        return tWizard('escalations.finalize_incomplete_reason')
    }
  }

  // True when leaving the Locations step now would discard an unsaved
  // per-location edit. Back/Cancel re-derive from saved truth on re-entry (they
  // navigate without persisting, unlike Continue — AI-1130), so they must
  // confirm before discarding (AI-996).
  const leaveWouldDiscardEdit = locationsHasUnsavedEdit

  const doCancel = () => {
    dispatch(push(returnsToActivation ? ACTIVATION_PATH : RECOVERY_AI_ROOT))
  }

  const handleCancel = () => {
    if (leaveWouldDiscardEdit) {
      setPendingLeave('cancel')
      return
    }
    doCancel()
  }

  // Back is pure navigation — no persist. In-memory wizard state survives
  // because WizardProvider is mounted above this route (in RecoveryAI, scoped to
  // /configure), so it is not torn down by :step navigation.
  const doBack = () => {
    // Clear any stale submit error so it does not linger on the previous step.
    setSubmitError(null)
    if (currentIndex > 0) {
      dispatch(push(buildConfigurePath(STEPS[currentIndex - 1].slug)))
    }
  }

  const handleBack = () => {
    if (leaveWouldDiscardEdit) {
      setPendingLeave('back')
      return
    }
    doBack()
  }

  // Confirm the pending leave: run the deferred navigation, then close the dialog.
  // The step unmounts on navigation, and useWizardLocations' cleanup clears
  // locationsUnsaved, so the guard resets itself.
  const confirmLeave = () => {
    const target = pendingLeave
    setPendingLeave(null)
    if (target === 'cancel') doCancel()
    else if (target === 'back') doBack()
  }

  const currentSlug = STEPS[currentIndex].slug

  const persistStep = async (onSuccess: () => void): Promise<void> => {
    setIsPersistingStep(true)
    try {
      setSubmitError(null)
      await submitStep(currentSlug, wizardState)
      // AI-1130: run whatever the CURRENTLY MOUNTED step registered (today, only
      // LocationsStep — see WizardContext.continueActionRef) before advancing.
      // null on every other step, so this is a no-op there. Awaited so a
      // rejection below skips onSuccess() exactly like a submitStep rejection
      // already does.
      await continueActionRef.current?.()
      // Cleared BEFORE onSuccess() (not in a `finally`, final review AI-1130):
      // onSuccess() navigates, and on the last step that unmounts Configure — a
      // `finally` here would run setIsPersistingStep(false) AFTER the
      // navigation/unmount, a state update on an unmounted component.
      setIsPersistingStep(false)
      onSuccess()
    } catch (err) {
      // Always show a translated generic error to merchants — backend
      // validation messages are authored for developer/support consumption
      // and may reference DB column names or internal identifiers. Logging
      // the raw error so on-call still has the detail.
      // eslint-disable-next-line no-console
      console.error('[RecoveryAI Wizard] submit failed', err)
      // The Locations step renders its own detailed save-error banner
      // (isSaveError / isConfigOnlyError in LocationsStep), driven by the same
      // mutations continueActionRef just called — showing the generic banner
      // too would duplicate/contradict it. Every other step still falls back to
      // the generic copy, unchanged. NOTE: this is only correct because
      // mapStepToConfig('locations') is a no-op ({}), so submitStep can never
      // itself reject for this step — if a future change ever gives 'locations'
      // real config-PUT fields, a genuine submitStep failure would go silent
      // here with no banner at all. Revisit this guard if that changes.
      if (currentSlug !== 'locations') setSubmitError(tWizard('submit_error'))
      setIsPersistingStep(false)
    }
  }

  const handleContinue = () => {
    // Defense-in-depth: Continue/Finalize is disabled when blocked, but guard the
    // handler too so a stale click can't fire the activation PUT / nav while
    // incomplete, while the current config failed to load (AI-580), or while the
    // Locations subset is empty (advancing past no selected location).
    if (blockFinalize || blockFinalizeUnhydrated || blockLocationsContinue)
      return
    persistStep(() => {
      if (!isLastStep) {
        // Train's Continue routes through the "Configuring your RecoveryAI"
        // interstitial (AI-242), which then advances to Testing after its
        // calibration delay; every other stage advances to the next STEPS slug.
        const nextPath =
          currentSlug === 'train'
            ? PROCESSING_PATH
            : buildConfigurePath(STEPS[currentIndex + 1].slug)
        dispatch(push(nextPath))
      } else {
        // Escalations is the final stage; its Finalize action persists
        // (incl. onboarding_completed) then routes to ConfiguredSummary
        // (ACTIVATION_PATH) — the pricing/activate CTA that fires the
        // provisioning enable — since Finalize left the config off. EXCEPT an
        // already-active re-entry (isAlreadyActive): it stays active, so send
        // it to the console, never the activate CTA (which would re-fire the
        // audited PUT on an already-running agent for no provisioning and a
        // false "activated" success page). The calibration interstitial runs
        // earlier (Train→Testing), so there is no intermediate Processing
        // screen here.
        const finalizeDestination = !isAlreadyActive
          ? ACTIVATION_PATH
          : SETTINGS_PATH
        dispatch(push(finalizeDestination))
      }
    })
  }

  const renderStep = () => {
    switch (step) {
      case 'train':
        return <PhilosophyAssessment />
      case 'testing':
        return <Testing onComplete={handleTestingComplete} />
      case 'locations':
        return <LocationsStep />
      case 'escalations':
        return <EscalationRouting />
      default:
        return null
    }
  }

  return (
    <div
      className={css({
        backgroundColor: merchantTheme.colors.grey05,
        minHeight: '100vh',
      })}
    >
      <Helmet>
        <title>{t('page_title')} - Setup</title>
      </Helmet>

      {/* Teal stepper bar. AI-1123: pinned just below the fixed app navbar so
          the Continue/Finalize button (and the coach-mark that anchors to it)
          stays in view when the merchant scrolls down through the simulator.
          `top` is the navbar height, not 0, or the app navbar (fixed, z-index
          900) would cover this bar's controls. */}
      <div
        data-testid="wizard-header-bar"
        className={css({
          position: 'sticky',
          top: APP_NAVBAR_HEIGHT,
          zIndex: WIZARD_STICKY_BAR_Z_INDEX,
          backgroundColor: merchantTheme.colors.spearmint50,
          height: '80px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 24px 0 32px',
        })}
      >
        <WizardStepper currentStep={step} />
        {/* ENG-984 (WCAG 4.1.3): sr-only note naming why Finalize is disabled
            while activation is incomplete, referenced by the button's
            aria-describedby above. Only rendered when it is actually the active
            cue (the config-load-error cue takes precedence and renders its own
            visible, id'd alert instead — see below). */}
        {continueCueId === ACTIVATION_INCOMPLETE_CUE_ID && (
          <span id={ACTIVATION_INCOMPLETE_CUE_ID} className="sr-only">
            {resolveActivationCueText()}
          </span>
        )}
        {/* WCAG 4.1.3: sr-only reason for the disabled Continue on the Locations
            step when the chosen subset is empty, referenced via aria-describedby. */}
        {continueCueId === LOCATIONS_EMPTY_SUBSET_CUE_ID && (
          <span id={LOCATIONS_EMPTY_SUBSET_CUE_ID} className="sr-only">
            {tWizard('locations.continue_empty_subset_reason')}
          </span>
        )}
        <WizardNavButtons
          onCancel={handleCancel}
          onContinue={handleContinue}
          // AI-1131: returnsToActivation is the re-test entry from Ready to
          // Activate's "Test your agent in sandbox" (always targeting the
          // Testing step) — the merchant already finished setup, so Back (to
          // the philosophy quiz) and Continue (to Locations) don't apply.
          // Only "Back to activation" (Cancel, below) makes sense here.
          onBack={
            currentIndex > 0 && !returnsToActivation ? handleBack : undefined
          }
          hideContinue={returnsToActivation}
          cancelLabel={
            returnsToActivation ? tWizard('back_to_activation') : undefined
          }
          // Still computed (and harmless) even though hideContinue unmounts the
          // button entirely on the returnsToActivation entry — none of these
          // gates read step-specific state that would be unsafe to evaluate here.
          continueDisabled={
            blockTrainContinue ||
            blockTestingContinue ||
            blockLocationsContinue ||
            blockFinalize ||
            blockFinalizeUnhydrated
          }
          continueLabel={
            isLastStep ? tWizard('escalations.finalize') : undefined
          }
          continueAriaDescribedby={continueCueId}
          continueTooltip={continueTooltip}
          isSubmitting={isSubmitting || isPersistingStep}
        />
      </div>

      {/* Step content */}
      <div
        className={css({
          padding: '48px 86px',
        })}
      >
        {submitError && (
          <div
            className={css({
              backgroundColor: merchantTheme.colors.cayenne10,
              color: merchantTheme.colors.cayenne50,
              padding: '12px 16px',
              borderRadius: '4px',
              marginBottom: '24px',
            })}
            role="alert"
          >
            {submitError}
          </div>
        )}
        {/* AI-580: a re-entry whose config GET failed can't safely Finalize (it
            would re-PUT wizard defaults over live config). Name the failure and
            point to the fix instead of dead-ending on a disabled button. Assertive
            alert; the disabled Finalize references it via aria-describedby (WCAG
            4.1.3). Takes precedence over the activation cue. */}
        {blockFinalizeUnhydrated && (
          <div
            className={css({
              maxWidth: WIZARD_CONTENT_MAX_WIDTH,
              marginLeft: 'auto',
              marginRight: 'auto',
            })}
          >
            <div className="mb-m" role="alert" id={CONFIG_LOAD_ERROR_CUE_ID}>
              <Notification kind="warning" size="full">
                <Text.BodyText4 bold className="d-block mb-xs">
                  {tWizard('escalations.finalize_config_error_title')}
                </Text.BodyText4>
                <Text.BodyText4 className="d-block">
                  {tWizard('escalations.finalize_config_error_body')}
                </Text.BodyText4>
              </Notification>
            </div>
          </div>
        )}
        {/* AI-117: shell-owned focus target for step navigation. tabIndex={-1}
            makes it programmatically focusable; outline:none suppresses the ring
            on this non-interactive container (the step's own controls keep theirs). */}
        <div
          ref={stepContentRef}
          tabIndex={-1}
          data-testid="wizard-step-content"
          className={css({ outline: 'none' })}
        >
          {renderStep()}
        </div>
      </div>

      {/* AI-996: confirm before Back/Cancel discards an unsaved Locations edit.
          Only ever opened when leaveWouldDiscardEdit held at click time. */}
      <ConfirmModal
        isOpen={pendingLeave !== null}
        title={tWizard('locations.leave_confirm_title')}
        description={tWizard('locations.leave_confirm_body')}
        confirmText={tWizard('locations.leave_confirm_cta')}
        onClose={() => setPendingLeave(null)}
        onConfirm={confirmLeave}
        isJustified
      />
    </div>
  )
}

export default Configure
